/**
 * Returns chart with trend-based coloring and benchmark support.
 * Based on the existing PerformanceChart pattern with additional
 * tooltip pnl field and benchmark messaging.
 */
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { formatCurrency, formatDate } from '@/utils/formatters';
import type { PerformanceDataPoint } from '@/types/performance';

interface ReturnsChartProps {
  data: PerformanceDataPoint[];
  showBenchmark?: boolean;
  benchmarkName?: string;
  isLoading?: boolean;
}

export function ReturnsChart({
  data,
  showBenchmark = false,
  benchmarkName = 'S&P 500',
  isLoading = false,
}: ReturnsChartProps) {
  const isPositive =
    data.length >= 2 ? data[data.length - 1]!.value > data[0]!.value : true;

  const lineColor = isPositive ? '#10b981' : '#f43f5e';
  const gradientId = isPositive ? 'returnsGreenGradient' : 'returnsRedGradient';

  // Compute a tight Y-axis domain with padding so trends are visible
  const values = data.map((d) => d.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;
  const padding = range > 0 ? range * 0.15 : maxValue * 0.02;
  const yDomain: [number, number] = [
    Math.max(0, Math.floor((minValue - padding) / 1000) * 1000),
    Math.ceil((maxValue + padding) / 1000) * 1000,
  ];

  if (isLoading) {
    return <div className="h-64 rounded shimmer" data-testid="chart-skeleton" />;
  }

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-dark-700 bg-dark-800/50">
        <p className="text-sm text-dark-500">No performance data available</p>
      </div>
    );
  }

  return (
    <div>
      {showBenchmark && (
        <p className="mb-2 text-xs text-dark-400">
          Benchmark data unavailable. Showing portfolio only.
        </p>
      )}

      <div className="h-64" role="img" aria-label={`Portfolio returns chart vs ${benchmarkName}`}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <defs>
              <linearGradient id="returnsGreenGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="returnsRedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#f43f5e" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => formatDate(value)}
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickLine={false}
              axisLine={{ stroke: '#334155' }}
            />
            <YAxis
              domain={yDomain}
              tickFormatter={(value) => formatCurrency(value)}
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickLine={false}
              axisLine={false}
              width={80}
            />
            <Tooltip
              content={<ReturnsTooltip />}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={lineColor}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 4, fill: lineColor, stroke: '#0f172a', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Accessible data table */}
      <table className="sr-only">
        <caption>Portfolio returns data</caption>
        <thead>
          <tr>
            <th>Date</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {data.map((point) => (
            <tr key={point.date}>
              <td>{formatDate(point.date)}</td>
              <td>{formatCurrency(point.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface TooltipPayloadEntry {
  payload: PerformanceDataPoint;
}

interface ReturnsTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
}

function ReturnsTooltip({ active, payload, label }: ReturnsTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const dataPoint = payload[0]!.payload;

  return (
    <div
      style={{
        backgroundColor: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '8px',
        padding: '8px 12px',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.3)',
      }}
    >
      <p style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>
        {formatDate(label ?? dataPoint.date)}
      </p>
      <p style={{ color: '#f1f5f9', fontSize: '13px', fontWeight: 500 }}>
        {formatCurrency(dataPoint.value)}
      </p>
      <p
        style={{
          color: dataPoint.pnl >= 0 ? '#10b981' : '#f43f5e',
          fontSize: '12px',
          marginTop: '2px',
        }}
      >
        Daily: {dataPoint.pnl >= 0 ? '+' : ''}
        {formatCurrency(dataPoint.pnl)}
      </p>
    </div>
  );
}
